import { useState, useEffect, useCallback, useRef } from 'react';
import { artifactNoteService } from '../services/artifactNoteService';
/**
 * Loads and mutates the per-feature note log for one saved artifact.
 * Pass `null` (e.g. for a feature that isn't saved to the server yet) to keep
 * the hook idle — it will report an empty, non-loading state and the caller
 * can gate the UI on whether an artifactId exists.
 */
export function useArtifactNotes(artifactId) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Guards against a stale list response landing after the selected artifact
    // changed (mirrors the generation-counter pattern used elsewhere).
    const requestGenRef = useRef(0);
    const refresh = useCallback(async () => {
        if (!artifactId) {
            setNotes([]);
            setError(null);
            setLoading(false);
            return;
        }
        const gen = ++requestGenRef.current;
        setLoading(true);
        setError(null);
        try {
            const list = await artifactNoteService.listNotes(artifactId);
            if (gen !== requestGenRef.current)
                return;
            setNotes(list);
        }
        catch (e) {
            if (gen !== requestGenRef.current)
                return;
            setError(e instanceof Error ? e.message : 'Failed to load notes');
        }
        finally {
            if (gen === requestGenRef.current)
                setLoading(false);
        }
    }, [artifactId]);
    useEffect(() => {
        refresh();
    }, [refresh]);
    const addNote = useCallback(async (content, files) => {
        if (!artifactId || !content.trim())
            return false;
        try {
            const resp = await artifactNoteService.createNote(artifactId, content.trim());
            if (!resp.success) {
                setError(resp.message || 'Failed to add note');
                return false;
            }
            if (files && files.length > 0) {
                // The note exists at this point, so a failed upload keeps the text and
                // surfaces the error rather than losing the entry.
                await Promise.all(files.map(file => artifactNoteService.uploadAttachment(resp.noteId, file)));
            }
            await refresh();
            return true;
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to add note');
            await refresh();
            return false;
        }
    }, [artifactId, refresh]);
    const editNote = useCallback(async (noteId, content) => {
        if (!content.trim())
            return false;
        try {
            const resp = await artifactNoteService.updateNote(noteId, content.trim());
            if (!resp.success) {
                setError(resp.message || 'Failed to update note');
                return false;
            }
            await refresh();
            return true;
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update note');
            return false;
        }
    }, [refresh]);
    const removeNote = useCallback(async (noteId) => {
        try {
            const resp = await artifactNoteService.deleteNote(noteId);
            if (!resp.success) {
                setError(resp.message || 'Failed to delete note');
                return false;
            }
            setNotes(prev => prev.filter(n => n.id !== noteId));
            return true;
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to delete note');
            return false;
        }
    }, []);
    const removeAttachment = useCallback(async (attachmentId) => {
        try {
            const resp = await artifactNoteService.deleteAttachment(attachmentId);
            if (!resp.success) {
                setError(resp.message || 'Failed to delete attachment');
                return false;
            }
            setNotes(prev => prev.map(n => ({
                ...n,
                attachments: n.attachments.filter(a => a.id !== attachmentId)
            })));
            return true;
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to delete attachment');
            return false;
        }
    }, []);
    return { notes, loading, error, refresh, addNote, editNote, removeNote, removeAttachment };
}
